import { useEffect, useMemo, useState } from "react";
import { CalendarDays, Plus, Trash2, Edit3, ChevronLeft, ChevronRight, Clock } from "lucide-react";
import {
  Page, Panel, SearchInput, DeleteButton,
  Modal, ModalActions, Field, SelectField, TextArea,
} from "../lib/ui";
import { api, humanDate, today } from "../lib/api";
import { CustomerCombobox } from "./Modals";

const STATUSES = ["Planlandı", "Tamamlandı", "İptal"];
const STATUS_TONE = { "Planlandı": "pending", "Tamamlandı": "paid", "İptal": "muted" };

const pad = (n) => String(n).padStart(2, "0");
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

function AppointmentModal({ customers, initial, onClose, onSaved, onCustomerAdded, defaultDate }) {
  const [form, setForm] = useState({
    customer_id: initial?.customer_id || null,
    customer_name: initial?.customer_name || "",
    title: initial?.title || "",
    date: initial?.date || defaultDate || today(),
    time: initial?.time || "10:00",
    plate: initial?.plate || "",
    phone: initial?.phone || "",
    note: initial?.note || "",
    status: initial?.status || "Planlandı",
  });
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      if (initial?.id) await api.put(`/appointments/${initial.id}`, form);
      else await api.post("/appointments", form);
      onSaved();
    } catch (ex) { setErr(ex.response?.data?.detail || "Kaydedilemedi"); }
  };
  return (
    <Modal title={initial?.id ? "Randevuyu Düzenle" : "Yeni Randevu"}
           subtitle="Muhasebe ve iş takip ekipleri ortaklaşa görür ve düzenler."
           onClose={onClose} testid="appointment-modal" size="lg">
      <form onSubmit={submit} data-testid="appointment-form">
        <div className="row">
          <div style={{ flex: 1 }}>
            <label className="field-label">Müşteri</label>
            <CustomerCombobox customers={customers} selectedId={form.customer_id} selectedName={form.customer_name}
                              onSelect={(c) => setForm({ ...form, customer_id: c?.id || null, customer_name: c?.name || "" })}
                              onCreateNew={onCustomerAdded} />
          </div>
          <SelectField label="Durum" testid="appointment-status" value={form.status}
                       onChange={(e) => setForm({ ...form, status: e.target.value })}>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </SelectField>
        </div>
        <div className="row">
          <Field label="Başlık / Konu" testid="appointment-title" value={form.title}
                 onChange={(e) => setForm({ ...form, title: e.target.value })} required placeholder="Örn. Yıllık bakım" />
          <Field label="Tarih" testid="appointment-date" type="date" value={form.date}
                 onChange={(e) => setForm({ ...form, date: e.target.value })} required />
          <Field label="Saat" testid="appointment-time" type="time" value={form.time}
                 onChange={(e) => setForm({ ...form, time: e.target.value })} />
        </div>
        <div className="row">
          <Field label="Plaka (opsiyonel)" testid="appointment-plate" value={form.plate}
                 onChange={(e) => setForm({ ...form, plate: e.target.value })} />
          <Field label="Telefon (opsiyonel)" testid="appointment-phone" value={form.phone}
                 onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        </div>
        <TextArea label="Not" testid="appointment-note" value={form.note} rows={2}
                  onChange={(e) => setForm({ ...form, note: e.target.value })} />
        {err && <div className="error-text">{err}</div>}
        <ModalActions onCancel={onClose} submitLabel="Kaydet" submitTestId="appointment-submit" />
      </form>
    </Modal>
  );
}

function MonthCalendar({ rows, cursor, setCursor, selectedDate, setSelectedDate }) {
  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const startIdx = (first.getDay() + 6) % 7; // Pzt=0
  const last = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
  const days = [];
  for (let i = 0; i < startIdx; i++) days.push(null);
  for (let d = 1; d <= last.getDate(); d++) days.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));
  while (days.length % 7 !== 0) days.push(null);
  const monthLabel = cursor.toLocaleDateString("tr-TR", { month: "long", year: "numeric" });
  const counts = useMemo(() => {
    const m = {};
    rows.forEach((r) => { m[r.date] = (m[r.date] || 0) + 1; });
    return m;
  }, [rows]);
  return (
    <div className="apt-cal" data-testid="appointment-calendar">
      <div className="apt-cal-head">
        <button className="icon-button" data-testid="cal-prev"
                onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}>
          <ChevronLeft size={16} />
        </button>
        <strong>{monthLabel}</strong>
        <button className="icon-button" data-testid="cal-next"
                onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}>
          <ChevronRight size={16} />
        </button>
      </div>
      <div className="apt-cal-grid">
        {["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"].map((d) => (
          <div key={d} className="apt-cal-weekday">{d}</div>
        ))}
        {days.map((d, i) => {
          if (!d) return <div key={i} className="apt-cal-cell blank" />;
          const key = ymd(d);
          const isToday = key === today();
          const isSelected = key === selectedDate;
          return (
            <button key={i} className={`apt-cal-cell${isToday ? " today" : ""}${isSelected ? " selected" : ""}`}
                    data-testid={`cal-day-${key}`} onClick={() => setSelectedDate(key)}>
              <span>{d.getDate()}</span>
              {counts[key] > 0 && <i className="apt-dot" title={`${counts[key]} randevu`}>{counts[key]}</i>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function WeekCalendar({ rows, cursor, setCursor, selectedDate, setSelectedDate }) {
  // Pzt başlangıçlı hafta
  const base = new Date(cursor);
  const dow = (base.getDay() + 6) % 7;
  base.setDate(base.getDate() - dow);
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(base); d.setDate(base.getDate() + i); return d;
  });
  const label = `${days[0].toLocaleDateString("tr-TR", { day: "numeric", month: "short" })} – ${days[6].toLocaleDateString("tr-TR", { day: "numeric", month: "short", year: "numeric" })}`;
  const byDay = useMemo(() => {
    const m = {};
    rows.forEach((r) => { (m[r.date] = m[r.date] || []).push(r); });
    Object.values(m).forEach((arr) => arr.sort((a, b) => (a.time || "").localeCompare(b.time || "")));
    return m;
  }, [rows]);
  const names = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];
  return (
    <div className="apt-week" data-testid="appointment-week">
      <div className="apt-cal-head">
        <button className="icon-button" data-testid="week-prev"
                onClick={() => { const d = new Date(cursor); d.setDate(d.getDate() - 7); setCursor(d); }}>
          <ChevronLeft size={16} />
        </button>
        <strong>{label}</strong>
        <button className="icon-button" data-testid="week-next"
                onClick={() => { const d = new Date(cursor); d.setDate(d.getDate() + 7); setCursor(d); }}>
          <ChevronRight size={16} />
        </button>
      </div>
      <div className="apt-week-grid">
        {days.map((d, i) => {
          const key = ymd(d);
          const list = byDay[key] || [];
          const isToday = key === today();
          const isSelected = key === selectedDate;
          return (
            <button key={key} className={`apt-week-col${isToday ? " today" : ""}${isSelected ? " selected" : ""}`}
                    data-testid={`week-day-${key}`} onClick={() => setSelectedDate(key)}>
              <div className="apt-week-head">
                <small>{names[i]}</small>
                <strong>{d.getDate()}</strong>
              </div>
              <div className="apt-week-items">
                {list.slice(0, 4).map((a) => (
                  <span key={a.id} className="apt-week-chip" title={`${a.time || ""} ${a.title || ""}`}>
                    {a.time || "—"} · {a.title || a.customer_name || "Randevu"}
                  </span>
                ))}
                {list.length > 4 && <small className="muted">+{list.length - 4}</small>}
                {list.length === 0 && <small className="muted">—</small>}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function Appointments({ user, customers = [], onCustomerAdded }) {
  const [rows, setRows] = useState([]);
  const [view, setView] = useState("month");  // "month" | "week"
  const [cursor, setCursor] = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const [selectedDate, setSelectedDate] = useState(today());
  const [modal, setModal] = useState(null);
  const [q, setQ] = useState("");

  const load = async () => {
    const { data } = await api.get("/appointments");
    setRows(data);
  };
  useEffect(() => { load(); }, []);

  const dayRows = useMemo(() => rows.filter((r) => r.date === selectedDate)
    .filter((r) => !q || [r.customer_name, r.title, r.plate, r.phone, r.note]
      .some((v) => (v || "").toLowerCase().includes(q.toLowerCase())))
    .sort((a, b) => (a.time || "").localeCompare(b.time || "")), [rows, selectedDate, q]);

  const del = async (id) => {
    if (!window.confirm("Randevu silinsin mi?")) return;
    try {
      await api.delete(`/appointments/${id}`);
      load();
    } catch (ex) { alert(ex.response?.data?.detail || "Silinemedi"); }
  };

  return (
    <>
      <Page eyebrow="ORTAK" title="Randevu Takvimi"
            subtitle="Muhasebe ve iş takip ekipleri birlikte kullanır. Günün randevuları takvimin altında."
            action={
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <div className="tab-row" data-testid="appointment-view-toggle" style={{ marginRight: 4 }}>
                  <button data-testid="view-month" className={view === "month" ? "tab active" : "tab"}
                          onClick={() => setView("month")}>Aylık</button>
                  <button data-testid="view-week" className={view === "week" ? "tab active" : "tab"}
                          onClick={() => setView("week")}>Haftalık</button>
                </div>
                <button className="primary-button" data-testid="appointment-add-btn"
                        onClick={() => setModal({ type: "new" })}>
                  <Plus size={14} /> Yeni Randevu
                </button>
              </div>
            } />
      <div className="appointment-layout">
        <Panel className="module-panel">
          {view === "month" ? (
            <MonthCalendar rows={rows} cursor={cursor} setCursor={setCursor}
                           selectedDate={selectedDate} setSelectedDate={setSelectedDate} />
          ) : (
            <WeekCalendar rows={rows} cursor={new Date(selectedDate)} setCursor={(d) => setSelectedDate(ymd(d))}
                          selectedDate={selectedDate} setSelectedDate={setSelectedDate} />
          )}
        </Panel>
        <Panel className="module-panel"
               title={new Date(selectedDate).toLocaleDateString("tr-TR", { day: "numeric", month: "long", weekday: "long" })}
               subtitle={`${dayRows.length} randevu`}
               action={<SearchInput value={q} onChange={setQ} testid="appointment-search" placeholder="Günde ara" />}>
          <div className="apt-day-list" data-testid="appointment-day-list">
            {dayRows.length === 0 && <p className="empty">Bu gün için randevu yok.</p>}
            {dayRows.map((r) => (
              <div key={r.id} className="apt-item" data-testid={`appointment-row-${r.id}`}>
                <div className="apt-time"><Clock size={13} /> {r.time || "—"}</div>
                <div className="apt-body">
                  <strong>{r.title || "Randevu"}</strong>
                  <small>
                    {r.customer_name || "Müşteri yok"}
                    {r.plate && ` · ${r.plate}`}
                    {r.phone && ` · ${r.phone}`}
                  </small>
                  {r.note && <p className="muted" style={{ margin: "4px 0 0", fontSize: 12 }}>{r.note}</p>}
                </div>
                <span className={`status-pill ${STATUS_TONE[r.status] || "pending"}`}>{r.status}</span>
                <div className="row-actions">
                  <button className="icon-button" title="Düzenle"
                          data-testid={`appointment-edit-${r.id}`}
                          onClick={() => setModal({ type: "edit", row: r })}>
                    <Edit3 size={14} />
                  </button>
                  {(user.role === "admin" || r.created_by === user.id) && (
                    <DeleteButton testid={`appointment-delete-${r.id}`} onClick={() => del(r.id)} />
                  )}
                </div>
              </div>
            ))}
          </div>
        </Panel>
      </div>
      {modal && (
        <AppointmentModal customers={customers} initial={modal.row || null}
                          defaultDate={selectedDate}
                          onClose={() => setModal(null)}
                          onSaved={() => { setModal(null); load(); }}
                          onCustomerAdded={onCustomerAdded} />
      )}
    </>
  );
}
